import { memo } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronRightIcon, GripVerticalIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type WrapperProps = {
  children: React.ReactNode;
  title: string;
  subTitle?: string;
  expanded: boolean;
  onChange: () => void;
  handleDelete: () => void;
  variant?: 'white' | 'grey';
  disabled?: boolean;
  isNested?: boolean;
  hideReorderIcon?: boolean;
};

const ToolkitAccordionWrapper = ({
  children,
  title,
  subTitle,
  expanded,
  onChange,
  handleDelete,
  variant = 'white',
  disabled = false,
  isNested = false,
  hideReorderIcon = false,
}: WrapperProps) => {
  const { t } = useTranslation();

  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      value={expanded ? 'item' : ''}
      onValueChange={() => onChange()}
      className={cn(
        'group/accordion rounded-md border border-border',
        isNested ? 'w-full' : 'w-[312px]'
      )}
    >
      <AccordionPrimitive.Item value="item">
        <AccordionPrimitive.Header className="group/header flex">
          <AccordionPrimitive.Trigger
            aria-controls={`${title}-content`}
            id={`${title}-header`}
            className={cn(
              'group/trigger relative flex min-w-0 flex-1 items-center gap-4 rounded-md py-2 pl-4 text-left outline-none',
              variant === 'grey' ? 'bg-muted/50' : 'bg-background'
            )}
          >
            {!hideReorderIcon && (
              <span
                className={cn(
                  'absolute top-1/2 left-0.5 -translate-y-1/2',
                  expanded ? 'invisible' : 'invisible group-hover/trigger:visible'
                )}
              >
                <GripVerticalIcon size={12} className="text-muted-foreground" />
              </span>
            )}

            {/* Title & variable section */}
            <div className="flex min-w-0 flex-1 items-center gap-4">
              {/* Title with chevron */}
              <div className="flex min-w-0 items-center gap-2">
                <ChevronRightIcon
                  size={16}
                  className="shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/trigger:rotate-90"
                />
                <span className="truncate text-sm leading-5 font-medium text-foreground">
                  {title}
                </span>
              </div>

              {/* Badge */}
              {subTitle && (
                <Badge
                  type="secondary"
                  flavour="neutral"
                  rounded={false}
                  className="max-w-32 truncate px-3 py-1 text-xs leading-4 font-semibold"
                >
                  {subTitle}
                </Badge>
              )}
            </div>
          </AccordionPrimitive.Trigger>

          {/* Delete button — outside Trigger to avoid button-inside-button */}
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.delete')}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            disabled={disabled}
            className={cn(
              'my-auto mr-2 shrink-0 hover:[&_svg]:text-destructive',
              expanded ? 'visible' : 'invisible group-hover/header:visible'
            )}
          >
            <Trash2Icon size={16} />
          </Button>
        </AccordionPrimitive.Header>
        <AccordionPrimitive.Content
          className={cn(
            'overflow-hidden rounded-md px-4 pb-4 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
            variant === 'grey' ? 'bg-muted/50' : 'bg-background'
          )}
        >
          {children}
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  );
};

export default memo(ToolkitAccordionWrapper);
